import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import vtcService from '../../services/vtc.service';
import { TZ } from '../../utils/date';
import { connectSocket, disconnectSocket } from '../../services/socket';

const statusColors = {
  requested: 'warning',
  accepted: 'info',
  arrived: 'info',
  started: 'primary',
  completed: 'success',
  cancelled: 'error',
};

const statusLabels = {
  requested: 'Demandée',
  accepted: 'Acceptée',
  arrived: 'Chauffeur arrivé',
  started: 'En cours',
  completed: 'Terminée',
  cancelled: 'Annulée',
};

const VTCRides = () => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    loadRides();

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const s = connectSocket(user.id);

    // Mise à jour de statut en temps réel
    s.on('vtc-status-changed', ({ rideId, status }) => {
      setRides(prev => prev.map(r => r.id === parseInt(rideId) ? { ...r, ride_status: status } : r));
    });

    // Course acceptée → rafraîchir pour avoir les infos chauffeur
    s.on('vtc-ride-accepted', () => loadRides());

    // Course terminée
    s.on('vtc-ride-completed', () => loadRides());

    return () => {
      s.off('vtc-status-changed');
      s.off('vtc-ride-accepted');
      s.off('vtc-ride-completed');
      disconnectSocket();
    };
  }, []);

  const loadRides = async () => {
    try {
      const data = await vtcService.getMyRides();
      setRides(data.rides || []);
    } catch (err) {
      setError('Erreur lors du chargement des courses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRide = async () => {
    if (!selectedRide || !cancelReason.trim()) {
      return;
    }

    try {
      await vtcService.cancelRide(selectedRide.id, cancelReason);
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedRide(null);
      loadRides();
    } catch (err) {
      setError('Erreur lors de l\'annulation');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h3" gutterBottom fontWeight={700}>
              Mes courses VTC
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Historique de vos courses
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/vtc"
            variant="contained"
            startIcon={<DirectionsCarIcon />}
          >
            Nouvelle course
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {rides.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <DirectionsCarIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucune course
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Vous n'avez pas encore réservé de course
            </Typography>
            <Button component={Link} to="/vtc" variant="contained">
              Demander une course
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {rides.map((ride) => (
            <Grid item xs={12} key={ride.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Course #{ride.ride_number}
                          </Typography>
                          <Chip
                            label={statusLabels[ride.ride_status] || ride.ride_status}
                            color={statusColors[ride.ride_status] || 'default'}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {new Date(ride.created_at).toLocaleDateString('fr-FR', {
                              timeZone: TZ, day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </Box>
                        <Chip
                          label={ride.vehicle_type}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="start" gap={2} mb={2}>
                          <MyLocationIcon color="success" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Départ
                            </Typography>
                            <Typography variant="body1">
                              {ride.pickup_address}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="start" gap={2}>
                          <LocationOnIcon color="error" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Arrivée
                            </Typography>
                            <Typography variant="body1">
                              {ride.dropoff_address}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box display="flex" gap={3}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {ride.estimated_duration || 'N/A'} min
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2">
                            Distance: {ride.distance_km} km
                          </Typography>
                        </Box>
                      </Box>

                      {ride.driver_first_name && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Chauffeur
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {ride.driver_first_name} {ride.driver_last_name}
                          </Typography>
                          {ride.vehicle_model && (
                            <Typography variant="caption" color="text.secondary">
                              {ride.vehicle_brand} {ride.vehicle_model} - {ride.vehicle_color}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 2,
                          height: '100%',
                        }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          Tarif
                        </Typography>
                        {ride.base_fare && (
                          <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="body2">Prise en charge</Typography>
                            <Typography variant="body2">{ride.base_fare} €</Typography>
                          </Box>
                        )}
                        {ride.distance_fare && (
                          <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="body2">Distance</Typography>
                            <Typography variant="body2">{ride.distance_fare} €</Typography>
                          </Box>
                        )}
                        {ride.time_fare && (
                          <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="body2">Temps</Typography>
                            <Typography variant="body2">{ride.time_fare} €</Typography>
                          </Box>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle1" fontWeight={600}>
                            Total
                          </Typography>
                          <Typography variant="subtitle1" fontWeight={600} color="primary">
                            {ride.total_fare} €
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <Chip
                            label={
                              ride.payment_status === 'paid'
                                ? 'Payé'
                                : ride.payment_status === 'pending'
                                ? 'En attente'
                                : 'Non payé'
                            }
                            color={ride.payment_status === 'paid' ? 'success' : 'warning'}
                            size="small"
                            sx={{ width: '100%' }}
                          />
                        </Box>

                        {ride.ride_status === 'requested' && (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={() => {
                              setSelectedRide(ride);
                              setCancelDialogOpen(true);
                            }}
                          >
                            Annuler
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  {ride.ride_status === 'completed' && ride.completed_at && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="success.dark">
                        <CheckIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />Terminée le{' '}
                        {new Date(ride.completed_at).toLocaleDateString('fr-FR', {
                          timeZone: TZ, day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  )}

                  {ride.ride_status === 'cancelled' && ride.cancellation_reason && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="error.dark">
                        Annulée: {ride.cancellation_reason}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Annuler la course</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Raison de l'annulation"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Fermer</Button>
          <Button onClick={handleCancelRide} color="error" disabled={!cancelReason.trim()}>
            Confirmer l'annulation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VTCRides;
